import React, { useEffect, useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, Spinner } from 'reactstrap';
import { useTranslator } from '@front-activerse/common-components';
import type { WearableProvider, WearableConnection } from './types';
import ConnectionStatus from './ConnectionStatus';

const SYNC_COOLDOWN_MINUTES = 10;

function getSyncCooldownRemainingMinutes(lastSyncedAt: string | null): number {
  if (!lastSyncedAt) return 0;
  const elapsed = (Date.now() - new Date(lastSyncedAt).getTime()) / 60_000;
  return Math.max(0, Math.ceil(SYNC_COOLDOWN_MINUTES - elapsed));
}

interface Props {
  provider: WearableProvider;
  connection?: WearableConnection;
  onConnect: (providerKey: string) => void;
  onDisconnect: (providerKey: string) => void;
  onSync: (providerKey: string) => void;
  connecting: boolean;
  disconnecting: boolean;
  syncing: boolean;
  isConfirming: boolean;
}

const ProviderCard: React.FC<Props> = ({
  provider,
  connection,
  onConnect,
  onDisconnect,
  onSync,
  connecting,
  disconnecting,
  syncing,
  isConfirming,
}) => {
  const { t, language } = useTranslator();
  const [showConfirm, setShowConfirm] = useState(false);
  const [, setTick] = useState(0);

  const isConnected = connection?.status === 'ACTIVE';
  const isPending = connection?.status === 'PENDING';
  const isComingSoon = provider.availability === 'COMING_SOON';

  const syncCooldownMinutes = getSyncCooldownRemainingMinutes(connection?.lastSyncedAt ?? null);
  const isSyncOnCooldown = syncCooldownMinutes > 0;

  useEffect(() => {
    if (!isConnected || !isSyncOnCooldown) return;
    const interval = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(interval);
  }, [isConnected, isSyncOnCooldown]);

  const formattedLastSync = connection?.lastSyncedAt
    ? new Intl.DateTimeFormat(language, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(connection.lastSyncedAt))
    : null;

  const handleDisconnectConfirm = () => {
    setShowConfirm(false);
    onDisconnect(provider.openWearablesProvider);
  };

  return (
    <>
      <div className="wearable-card mb-m">
        {/* Top row: logo + info + action */}
        <div className="wearable-card__top">
          <div className="wearable-card__logo">
            {provider.iconUrl ? (
              <img
                src={provider.iconUrl}
                alt={provider.displayName}
                className="provider-logo"
                style={{ height: 48, width: 'auto' }}
              />
            ) : null}
          </div>

          <div className="wearable-card__info">
            <div className="wearable-card__name-row">
              <span className="provider-name">{provider.displayName}</span>
              {isConnected && <div className="status-dot" />}
            </div>

            {isConnected && formattedLastSync && (
              <div className="last-sync">
                {t('wearables.provider.lastSync.label', { date: formattedLastSync })}
              </div>
            )}
          </div>

          <div className="wearable-card__action">
            {isComingSoon ? (
              <ConnectionStatus status="COMING_SOON" />
            ) : isConnected ? (
              <span
                role="button"
                className="disconnect-link text-text-error-color"
                onClick={() => !disconnecting && setShowConfirm(true)}
                data-test-id={`wearable_disconnect_${provider.openWearablesProvider}_button`}
              >
                {t('wearables.provider.disconnect.button')}
              </span>
            ) : isConfirming ? (
              <Button color="primary" size="sm" disabled>
                <Spinner size="sm" className="me-1" />
                {t('wearables.status.connecting')}
              </Button>
            ) : !isPending ? (
              <Button
                color="primary"
                size="sm"
                onClick={() => onConnect(provider.openWearablesProvider)}
                disabled={connecting}
                data-test-id={`wearable_connect_${provider.openWearablesProvider}_button`}
              >
                {t('wearables.provider.connect.button')}
              </Button>
            ) : null}
          </div>
        </div>

        {/* Bottom row: sync/verifying button */}
        {(isPending || isConnected) && (
          <div className="wearable-card__bottom">
            {isPending ? (
              <Button
                color="secondary"
                size="sm"
                disabled
                data-test-id={`wearable_confirm_${provider.openWearablesProvider}_button`}
              >
                <Spinner size="sm" className="me-1" />
                {t('wearables.provider.verifying.button')}
              </Button>
            ) : isConnected ? (
              <Button
                color="primary"
                size="sm"
                outline={isSyncOnCooldown}
                className={isSyncOnCooldown ? 'sync-cooldown' : ''}
                onClick={() => onSync(provider.openWearablesProvider)}
                disabled={syncing || isSyncOnCooldown}
                data-test-id={`wearable_sync_${provider.openWearablesProvider}_button`}
              >
                {syncing ? <Spinner size="sm" className="me-1" /> : null}
                {isSyncOnCooldown
                  ? t('wearables.provider.syncCooldown.button', { minutes: syncCooldownMinutes })
                  : t('wearables.provider.sync.button')}
              </Button>
            ) : null}
          </div>
        )}
      </div>

      <Modal isOpen={showConfirm} toggle={() => setShowConfirm(false)} centered size="md">
        <ModalHeader toggle={() => setShowConfirm(false)} />
        <ModalBody className="pt-0">
          <h2 className="title-medium semi-bold text-center">
            {t('wearables.disconnectConfirm.title', { provider: provider.displayName })}
          </h2>
          <p className="text-center text-text-variant-color">
            {t('wearables.disconnectConfirm.description', { provider: provider.displayName })}
          </p>
          <div className="disconnect-confirm-buttons mt-l">
            <Button
              color="primary"
              className="w-100"
              onClick={handleDisconnectConfirm}
              disabled={disconnecting}
            >
              {t('wearables.disconnectConfirm.confirm.button')}
            </Button>
            <Button
              color="secondary"
              className="w-100"
              outline
              onClick={() => setShowConfirm(false)}
            >
              {t('wearables.disconnectConfirm.cancel.button')}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default ProviderCard;
